import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, AlertCircle } from 'lucide-react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveToastProps {
  status: SaveStatus;
}

/**
 * Composant Toast pour afficher le statut de sauvegarde
 * Positionné en haut à droite de l'écran, visible même après scroll
 * Utilise un Portal pour éviter d'affecter le layout de la page
 */
export default function SaveToast({ status }: SaveToastProps) {
  const { t } = useTranslation('settings');
  if (status === 'idle') return null;

  return createPortal(
    <div className="fixed top-20 right-4 z-50 animate-fade-in">
      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg ${
        status === 'saving' ? 'bg-white border border-gray-200' :
        status === 'saved' ? 'bg-green-50 border border-green-200' :
        'bg-red-50 border border-red-200'
      }`}>
        {status === 'saving' && (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            <span className="text-sm text-gray-600">{t('autoSave.saving')}</span>
          </>
        )}
        {status === 'saved' && (
          <>
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-600 font-medium">{t('autoSave.saved')}</span>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-600 font-medium">{t('autoSave.error')}</span>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
